/**
 * Timeline utilities for date calculations, quarter grouping, and event positioning
 * Implements requirements 2.1, 2.5, and 4.1
 */

import { TimelineEvent, TimelineQuarter, EventPosition, TimelineConfig } from '@/interfaces/timeline';

/**
 * Default timeline configuration
 */
export const DEFAULT_TIMELINE_CONFIG: TimelineConfig = {
  quarterColors: [
    '#e3f2fd', // Light blue - Q1
    '#f3e5f5', // Light purple - Q2  
    '#e8f5e8', // Light green - Q3
    '#fff3e0'  // Light orange - Q4
  ],
  monthSpacing: 12, // pixels between months
  quarterSpacing: 24, // pixels between quarters
  eventBubbleSize: 140, // width/height of event bubbles
  timelineHeight: 400 // total timeline height
};

/**
 * Generate quarters from a date range
 * Requirement 4.1: Timeline organization with quarter grouping
 */
export function generateQuartersFromRange(startDate: Date, endDate: Date, config: TimelineConfig = DEFAULT_TIMELINE_CONFIG): TimelineQuarter[] {
  const quarters: TimelineQuarter[] = [];
  
  // Start from the beginning of the quarter containing startDate
  let currentDate = new Date(startDate.getFullYear(), Math.floor(startDate.getMonth() / 3) * 3, 1);
  
  while (currentDate <= endDate) {
    const year = currentDate.getFullYear();
    const quarter = Math.floor(currentDate.getMonth() / 3) + 1;
    
    const quarterStart = new Date(year, (quarter - 1) * 3, 1);
    const quarterEnd = new Date(year, quarter * 3, 0); // Last day of quarter
    
    // Generate months for this quarter
    const months: Date[] = [];
    for (let month = 0; month < 3; month++) {
      months.push(new Date(year, (quarter - 1) * 3 + month, 1));
    }

    quarters.push({
      year,
      quarter,
      startDate: quarterStart,
      endDate: quarterEnd,
      backgroundColor: config.quarterColors[(quarter - 1) % config.quarterColors.length],
      months
    });

    // Move to next quarter
    currentDate = new Date(year, quarter * 3, 1);
  }

  return quarters;
}

/**
 * Generate quarters from events
 * Requirement 4.1: Timeline organization based on event dates
 */
export function generateQuartersFromEvents(events: TimelineEvent[], config: TimelineConfig = DEFAULT_TIMELINE_CONFIG): TimelineQuarter[] {
  if (events.length === 0) return [];

  const dates = events.map(e => e.date);
  const startDate = new Date(Math.min(...dates.map(d => d.getTime())));
  const endDate = new Date(Math.max(...dates.map(d => d.getTime())));

  return generateQuartersFromRange(startDate, endDate, config);
}

/**
 * Calculate alternating positions for events
 * Requirement 2.1: Events alternating above and below timeline
 * Requirement 2.5: Chronological positioning
 */
export function calculateEventPositions(
  events: TimelineEvent[], 
  quarters: TimelineQuarter[],
  config: TimelineConfig = DEFAULT_TIMELINE_CONFIG
): EventPosition[] {
  const positions: EventPosition[] = [];
  
  // Sort events by date to ensure chronological order
  const sortedEvents = [...events].sort((a, b) => a.date.getTime() - b.date.getTime());
  
  // Calculate timeline width and positions
  const monthWidth = 100; // Base width for each month
  
  for (let i = 0; i < sortedEvents.length; i++) {
    const event = sortedEvents[i];
    
    // Find which quarter and month this event belongs to
    const quarter = quarters.find(q => 
      event.date >= q.startDate && event.date <= q.endDate
    );
    
    if (!quarter) {
      console.warn(`Event ${event.id} date ${event.date} does not fall within any quarter`);
      continue;
    }
    
    // Calculate X position based on date within the timeline
    const x = calculateXPositionForDate(event.date, quarters, monthWidth, config);
    
    // Determine Y position based on alternating above/below
    // Above events have negative Y, below events have positive Y
    const position: 'above' | 'below' = i % 2 === 0 ? 'above' : 'below';
    const y = position === 'above' 
      ? -(config.eventBubbleSize / 2 + 20) // 20px gap from timeline
      : (config.eventBubbleSize / 2 + 20);
    
    // Find the month this event belongs to
    const eventMonth = new Date(event.date.getFullYear(), event.date.getMonth(), 1);
    
    positions.push({
      eventId: event.id,
      position,
      x,
      y,
      width: config.eventBubbleSize,
      height: config.eventBubbleSize,
      month: eventMonth,
      quarter
    });
  }
  
  return positions;
}

/**
 * Calculate X position for a specific date within the timeline
 */
function calculateXPositionForDate(
  date: Date, 
  quarters: TimelineQuarter[], 
  monthWidth: number,
  config: TimelineConfig
): number {
  let x = 0;
  
  for (const quarter of quarters) {
    if (date >= quarter.startDate && date <= quarter.endDate) {
      // Add position within this quarter
      const monthIndex = date.getMonth() - quarter.startDate.getMonth();
      const dayProgress = (date.getDate() - 1) / new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
      
      x += monthIndex * (monthWidth + config.monthSpacing);
      x += dayProgress * monthWidth;
      break;
    } else if (date > quarter.endDate) {
      // Add full quarter width
      x += 3 * (monthWidth + config.monthSpacing) + config.quarterSpacing;
    }
  }
  
  return x;
}

/**
 * Calculate date range from events
 * Requirement 4.1: Timeline bounds calculation
 */
export function calculateDateRange(events: TimelineEvent[]): { start: Date; end: Date } {
  if (events.length === 0) {
    const now = new Date();
    return { start: now, end: now };
  }

  const dates = events.map(e => e.date.getTime());
  return {
    start: new Date(Math.min(...dates)),
    end: new Date(Math.max(...dates))
  };
}

/**
 * Get quarter for a specific date
 * Utility function for component usage
 */
export function getQuarterForDate(date: Date): { year: number; quarter: number } {
  return {
    year: date.getFullYear(),
    quarter: Math.floor(date.getMonth() / 3) + 1
  };
}

/**
 * Get month name for display
 * Utility function for component usage
 */
export function getMonthName(date: Date, short = false): string {
  const options: Intl.DateTimeFormatOptions = { 
    month: short ? 'short' : 'long' 
  };
  return date.toLocaleDateString('en-US', options);
}

/**
 * Get quarter name for display
 * Utility function for component usage
 */
export function getQuarterName(year: number, quarter: number): string {
  return `Q${quarter} ${year}`;
}

/**
 * Check if two dates are in the same month
 * Utility function for event grouping
 */
export function isSameMonth(date1: Date, date2: Date): boolean {
  return date1.getFullYear() === date2.getFullYear() && 
         date1.getMonth() === date2.getMonth();
}

/**
 * Check if two dates are in the same quarter
 * Utility function for quarter grouping
 */
export function isSameQuarter(date1: Date, date2: Date): boolean {
  const q1 = getQuarterForDate(date1);
  const q2 = getQuarterForDate(date2);
  return q1.year === q2.year && q1.quarter === q2.quarter;
}

/**
 * Validate event positioning to prevent overlaps
 * Requirement 2.1: Proper event positioning
 */
export function validateEventPositions(positions: EventPosition[]): boolean {
  // Check for overlapping positions
  for (let i = 0; i < positions.length; i++) {
    for (let j = i + 1; j < positions.length; j++) {
      const pos1 = positions[i];
      const pos2 = positions[j];
      
      // Check if positions overlap (simple bounding box check)
      const overlap = !(
        pos1.x + (pos1.width || 0) < pos2.x ||
        pos2.x + (pos2.width || 0) < pos1.x ||
        pos1.y + (pos1.height || 0) < pos2.y ||
        pos2.y + (pos2.height || 0) < pos1.y
      );
      
      if (overlap) {
        console.warn(`Event positions overlap: ${pos1.eventId} and ${pos2.eventId}`);
        return false;
      }
    }
  }
  
  return true;
}